// @flow

// force flow to type check this file - https://github.com/zapier/zapier/pull/31127
import * as React from 'react';

import {
  type ColorName,
  Colors,
  Screen,
  Typography,
  type TypographyName,
} from '@zapier/theme';
import { css, jsx } from '@emotion/core';

/** @jsx jsx */

type ChangeableProps = {
  color?: ColorName,
  type?: TypographyName,
};

type TypographyQuery = {
  [string]: ChangeableProps,
};

type Props = {
  /**
   * Text to be rendered. Accepts a Node to support minor variations, e.g., a
   * link within a heading, or an <em> tag for semantic emphasis.
   */
  children: React.Node,
  /** The color of text to render. */
  color: ColorName,
  /** An object with keys of media queries and values of ChangeableProps objects */
  queries?: TypographyQuery,
  /**
   * The type (effectively the "style") of text to render. One of a list of
   * preset types, which correspond exactly to the Zapier Visual Identity:
   * https://github.com/zapier/zapier-ui/issues/36
   */
  type?: TypographyName, // we have many usages of <Text> where type is not passed. Make optional for now.
};

/**
 * Converts queries object into CSS media queries that will change the
 * typography type
 * Query can be a key from @zapier/theme/Screen/queries or a plain string
 * in either `@media screen and (min-width: 100px)` or `(min-width: 100px)`
 * format
 */
const convertQueries = (queries: TypographyQuery = {}) =>
  Object.entries(queries).reduce((acc, [query, propsValue]: [string, *]) => {
    // This is only to appease Flow so we can type propsValue
    const props: ChangeableProps = (propsValue: any);
    const translatedQuery = Screen.queries[query] || query;
    const fullQuery = translatedQuery.startsWith('@media')
      ? translatedQuery
      : `@media screen and ${translatedQuery}`;
    return {
      ...acc,
      [fullQuery]: {
        ...(props.color && { color: Colors[props.color] }),
        ...(props.type && Typography[props.type]),
      },
    };
  }, {});

/**
 * Styles text according to the Zapier Visual Identity proposal:
 * https://github.com/zapier/zapier-ui/issues/36
 *
 * This component precisely implements the Zapier Visual Identity
 * for text. If you feel the need to add a variation on its
 * styles, your design is likely violating Zapier's style guide.
 *
 * This component is intended to be used as an inline element. Potential APIs
 * were discussed here:
 * https://github.com/zapier/zapier-ui/issues/36
 */
const Text = (props: Props) => {
  const textCss = css(
    {
      fontFamily: '"Open Sans", sans-serif',
      ...Typography[props.type],
      color: Colors[props.color],
      // Name it for debugging.
      label: `-${props.type || ''}--${props.color}-`,
    },
    props.queries && convertQueries(props.queries)
  );

  return <span css={textCss}>{props.children}</span>;
};

Text.defaultProps = {
  color: 'deepSpace',
};

export default Text;
